import type { RigaAnalisi } from './calcoli.ts';
import { creaXlsx } from './xlsx.ts';

/**
 * Esportazione dei dati analizzati.
 *
 * Tre formati con tre destinatari diversi: CSV per chi lo importa nelle
 * paghe, Excel (.xlsx) per chi ci lavora dentro, PDF per chi lo stampa o lo
 * allega.
 *
 * Sono generati nel browser dai dati gia' a schermo, cosi' l'esportazione
 * rispetta esattamente i filtri applicati: scaricare qualcosa di diverso da
 * quello che si sta guardando e' un ottimo modo per far perdere fiducia in un
 * report.
 */

export const INTESTAZIONI = [
  'Sede',
  'Reparto',
  'Dipendente',
  'GG ferie',
  'ORE ferie',
  'ORE recupero',
  'ORE legge 104',
  'ORE MF',
  'ORE donazione sangue',
  'ORE malattia',
  'ORE congedo paternità',
  'GG riposo produz.',
  'ORE totali (escl. 104, MF, CP, DS, malattia)',
] as const;

/**
 * Quante colonne iniziali sono testo. Dalla successiva in poi sono numeri.
 *
 * Serve a Excel, che senza il tipo giusto non somma la colonna, e al PDF per
 * allineare a destra. Stava scritto a mano in tutti e due i punti: togliendo
 * una colonna bastava dimenticarne uno perche' le cifre diventassero testo.
 */
export const COLONNE_TESTO = 3;

export function righeTabellari(righe: readonly RigaAnalisi[]): string[][] {
  return righe.map((r) => [
    r.sede,
    r.reparto,
    r.dipendente,
    virgola(r.giorniFerie),
    virgola(r.oreFerie),
    virgola(r.oreRecupero),
    virgola(r.ore104),
    virgola(r.oreMf),
    virgola(r.oreDs),
    virgola(r.oreMalattia),
    virgola(r.oreCp),
    virgola(r.giorniRiposo),
    virgola(r.oreTotali),
  ]);
}

/**
 * Il periodo in parole: "agosto 2026", oppure "Totale 2026".
 *
 * Non e' piu' una colonna della tabella - e' identico su tutte le righe, e
 * ripeterlo centottantatre volte non aggiungeva niente a quello che dicono
 * gia' i filtri in cima. Resta come sottotitolo del PDF e dentro il nome dei
 * file scaricati, dove serve a sapere cosa si ha in mano fra sei mesi.
 */
export function etichettaPeriodo(periodo: string): string {
  if (periodo.length === 4) return `Totale ${periodo}`;
  const [anno, mese] = periodo.split('-');
  return `${MESI[Number(mese) - 1] ?? mese} ${anno}`;
}

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/** I decimali con la virgola: e' come li legge Excel in italiano. */
function virgola(n: number): string {
  return String(n).replace('.', ',');
}

function scarica(contenuto: BlobPart, nome: string, tipo: string): void {
  const url = URL.createObjectURL(new Blob([contenuto], { type: tipo }));
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  ancora.click();
  URL.revokeObjectURL(url);
}

export function esportaCsv(righe: readonly RigaAnalisi[], nome: string): void {
  const virgolette = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const contenuto = [
    INTESTAZIONI.join(';'),
    ...righeTabellari(righe).map((r) => r.map(virgolette).join(';')),
  ].join('\r\n');
  // Il BOM evita che Excel interpreti gli accenti come simboli.
  scarica('﻿' + contenuto, `${nome}.csv`, 'text/csv;charset=utf-8');
}

/**
 * Excel vero: un .xlsx, non un XML travestito.
 *
 * Il formato precedente - SpreadsheetML con estensione .xls - faceva comparire
 * ad ogni apertura "Il formato e l'estensione non corrispondono. Il file
 * potrebbe essere danneggiato o non sicuro". Un avviso di sicurezza su un
 * report aziendale mette chi lo riceve nella posizione di dover decidere se
 * fidarsi, e la risposta prudente e' non aprirlo.
 *
 * Il pacchetto lo scrive analisi/xlsx.ts, senza librerie.
 */
export function esportaExcel(righe: readonly RigaAnalisi[], nome: string): void {
  const pacchetto = creaXlsx({
    nome: 'Analisi',
    intestazioni: INTESTAZIONI,
    righe: righeTabellari(righe),
    colonneTesto: COLONNE_TESTO,
  });

  scarica(
    pacchetto,
    `${nome}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/**
 * PDF tramite la stampa del browser.
 *
 * Generare un PDF nel browser richiederebbe una libreria pesante per ottenere
 * un risultato peggiore del motore di stampa, che sa gia' impaginare tabelle
 * lunghe, ripetere l'intestazione a ogni pagina e produrre un PDF con "Salva
 * come PDF". Si apre una finestra con la sola tabella e si lascia stampare.
 */
export function esportaPdf(
  righe: readonly RigaAnalisi[],
  titolo: string,
  sottotitolo: string,
): void {
  const finestra = window.open('', '_blank');
  if (!finestra) return;

  const sicuro = (v: string) =>
    v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const corpo = righeTabellari(righe)
    .map(
      (r) =>
        `<tr>${r
          .map((c, i) => `<td class="${i >= COLONNE_TESTO ? 'num' : ''}">${sicuro(c)}</td>`)
          .join('')}</tr>`,
    )
    .join('');

  finestra.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8">
<title>${sicuro(titolo)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: 'Segoe UI', Calibri, Arial, sans-serif; font-size: 10px; }
  h1 { font-size: 15px; margin: 0 0 2px; }
  p { color: #555; margin: 0 0 10px; font-size: 11px; }
  table { border-collapse: collapse; width: 100%; }
  th { background: #2e5090; color: #fff; text-align: left; }
  th, td { border: 1px solid #b9bec7; padding: 3px 6px; }
  /* Intestazione e cifre allineate allo stesso bordo: una colonna di numeri
     con il titolo a sinistra si legge male tanto sulla carta quanto a video. */
  th.num, td.num { text-align: right; }
  /* Ripete l'intestazione su ogni pagina di una tabella lunga. */
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
</style></head><body>
<h1>${sicuro(titolo)}</h1><p>${sicuro(sottotitolo)}</p>
<table><thead><tr>${INTESTAZIONI.map(
  (t, i) => `<th class="${i >= COLONNE_TESTO ? 'num' : ''}">${t}</th>`,
).join('')}</tr></thead>
<tbody>${corpo}</tbody></table>
</body></html>`);
  finestra.document.close();
  finestra.focus();
  finestra.print();
}
